/**
 * First-beat section of the Song & Timing workspace (W2-08 timing; DESIGN.md §4.15 "FIRST BEAT", §5.2 "First
 * beat (offset)"):
 *  - the offset as one sentence, "Beat 0 is heard at 0.120 s" (META.offset plus info.yml offset: what Phira hears);
 *  - -/+ buttons (1 ms, Shift 10 ms; also Alt+, / Alt+.) and the value in ms for typing an exact number;
 *  - Detect: lines the grid up with the song and reports its confidence;
 *  - the imported Phira per-chart adjustment (info.yml offset) with "Fold into chart offset".
 */

import { useId, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import type { AppServices } from '../../app/registry/types';
import { Button, Icon, IconButton, InfoPopover, Tooltip } from '../../ui';
import { m } from './timing.messages';
import { detectOffset } from './timingActions';
import type { SongAnalysis } from './timingAnalysis';
import type { TimingView } from './timingDoc';
import { foldPerChart, nudgeOffset, setHeardOffset } from './timingEdits';
import type { TimingText } from './timingHooks';
import { perChartAdjustmentMs } from './timingModel';
import s from './Timing.module.css';

export interface OffsetPanelProps {
  readonly services: AppServices;
  readonly text: TimingText;
  readonly view: TimingView;
  readonly analysis: SongAnalysis | null;
}

/** Offset in ms as typed text ("120" or "120.5"). */
function msText(ms: number): string {
  return String(Math.round(ms * 1000) / 1000);
}

export function OffsetPanel(props: OffsetPanelProps) {
  const { services, text, view } = props;
  const id = useId();
  const [draft, setDraft] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState<{ readonly heardMs: number; readonly confidence: number } | null>(null);
  const adjustment = perChartAdjustmentMs({ infoOffsetSec: view.infoOffsetSec });
  const noSong = view.music === null;

  const typed = draft === null ? null : Number(draft.trim().replace(',', '.'));
  const invalid = draft !== null && (typed === null || !Number.isFinite(typed));

  const commit = (): void => {
    if (draft === null) return;
    if (typed !== null && Number.isFinite(typed)) {
      if (Math.abs(typed - view.heardMs) > 1e-6) setHeardOffset(services, typed);
      setDraft(null);
    }
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Escape' && draft !== null) {
      e.preventDefault();
      e.stopPropagation();
      setDraft(null);
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      setDraft(null);
      nudgeOffset(services, (e.key === 'ArrowUp' ? 1 : -1) * (e.shiftKey ? 10 : 1));
    }
  };

  const detect = (): void => {
    setBusy(true);
    void detectOffset(services)
      .then((r) => {
        if (r !== null) setLast({ heardMs: r.offsetMs, confidence: r.confidence });
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <div className={s.stack}>
      <p className={s.sentence} data-testid="timing-beat-zero" aria-live="polite">
        {text.t(m.beatZeroAt, { sec: view.heardMs / 1000 })}
      </p>
      <p className={s.help}>{text.t(m.beatZeroHelp)}</p>
      <div className={s.rowTight}>
        {/* The Alt+, / Alt+. keys are in the buttons' tooltips (UX-082: the visible hints wrapped). */}
        <Tooltip content={text.t(m.offsetEarlier)} command="timing.offsetEarlier">
          <IconButton
            icon="Minus"
            size="sm"
            label={{ key: m.offsetEarlier }}
            onClick={(e) => {
              nudgeOffset(services, e.shiftKey ? -10 : -1);
            }}
          />
        </Tooltip>
        <label className="visually-hidden" htmlFor={id}>
          {text.t(m.offsetValueLabel)}
        </label>
        <input
          id={id}
          className={s.input}
          style={{ width: '6.5em' }}
          inputMode="decimal"
          autoComplete="off"
          spellCheck={false}
          value={draft ?? msText(view.heardMs)}
          aria-invalid={invalid}
          data-testid="timing-offset-ms"
          onChange={(e) => {
            setDraft(e.target.value);
          }}
          onKeyDown={onKeyDown}
          onBlur={() => {
            commit();
            setDraft(null);
          }}
        />
        <span className={s.subtle}>{text.t(m.offsetUnit)}</span>
        <Tooltip content={text.t(m.offsetLater)} command="timing.offsetLater">
          <IconButton
            icon="Plus"
            size="sm"
            label={{ key: m.offsetLater }}
            onClick={(e) => {
              nudgeOffset(services, e.shiftKey ? 10 : 1);
            }}
          />
        </Tooltip>
      </div>
      <div className={s.row}>
        <Tooltip content={text.t(m.detectOffsetTip)}>
          <Button
            icon="Crosshair"
            onClick={detect}
            disabledReason={noSong ? { key: m.noSongReason } : busy ? { key: m.detecting } : null}
            data-testid="timing-detect-offset"
          >
            {text.t(m.detect)}
          </Button>
        </Tooltip>
        {last !== null && Math.abs(last.heardMs - view.heardMs) < 0.5 && (
          <span className={s.strength}>
            <Icon name={last.confidence >= 0.5 ? 'CircleCheck' : 'TriangleAlert'} size={14} />
            {text.t(m.offsetConfidence, { pct: Math.round(last.confidence * 100) })}
          </span>
        )}
      </div>
      {adjustment !== 0 && (
        <div className={s.notice} data-testid="timing-per-chart">
          <Icon name="Info" size={16} />
          <div className={s.grow}>
            <div className={s.row}>
              <span>{text.t(m.phiraAdjust)}</span>
              <span className={s.mono}>
                {text.t(m.phiraAdjustValue, { sign: adjustment > 0 ? '+' : '-', sec: Math.abs(adjustment) / 1000 })}
              </span>
              <InfoPopover text={{ key: m.phiraAdjustHelp }} />
            </div>
            <Button
              size="sm"
              icon="Merge"
              onClick={() => {
                foldPerChart(services);
              }}
            >
              {text.t(m.foldIn)}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
